import { View, Text } from "react-native";
import { ArrowRight, CheckCircle2, Circle } from "lucide-react-native";
import StatusBadge from "./StatusBadge";
import { EMBER } from "../../onboarding/theme";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { CardSurface, CompactActionButton } from "@louise/moon-ui";
import {
  deriveCarrierCompensationSignals,
  resolveCarrierCompensationReadiness,
} from "./carrierCompensationReadiness";
import { semanticColors, withAlpha, gradients } from "@louise/theme/tokens";
import { LinearGradient } from "expo-linear-gradient";

type Role = "donor" | "carrier";

const ROLE_COPY: Record<Role, string> = {
  donor:
    "Compensation ranges for egg and sperm donors vary by clinic, location, and program. We're partnering with clinics to surface reliable estimates here. In the meantime, the donor registry under Find Care lists clinics actively recruiting.",
  carrier:
    "Compensation for surrogates varies by state, agency, and prior experience. We're working with agencies to surface reliable ranges here. In the meantime, the surrogacy section under Find Care lists agencies and resources.",
};

type Props = {
  role: Role;
  onOpenMedicalHistory?: () => void;
};

export default function CompensationPlaceholderCard({
  role,
  onOpenMedicalHistory,
}: Props) {
  const { data: patient } = usePatient();

  if (role === "carrier") {
    const readiness = resolveCarrierCompensationReadiness(
      deriveCarrierCompensationSignals(patient),
    );

    return (
      <CardSurface style={{ marginTop: 16, padding: 20, gap: 12 }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 16,
              fontWeight: "600",
              color: EMBER.colors.text,
            }}
          >
            Estimated compensation
          </Text>
          <StatusBadge type="coming-soon" />
        </View>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: EMBER.colors.textLight,
            lineHeight: 20,
          }}
        >
          {readiness.isReady
            ? "Your profile has enough context for a more tailored agency review. Louise will not promise eligibility or a specific amount."
            : "Add more context to unlock a more tailored estimate. This helps an agency review your situation; it does not guarantee eligibility or compensation."}
        </Text>

        <View style={{ gap: 8 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "700",
                color: EMBER.colors.text,
              }}
            >
              Estimate profile
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "700",
                color: EMBER.colors.textLight,
              }}
            >
              {readiness.completedCount} of {readiness.totalCount}
            </Text>
          </View>
          <View
            style={{
              height: 6,
              overflow: "hidden",
              borderRadius: 999,
              backgroundColor: withAlpha(semanticColors.accent.default, 0.12),
            }}
          >
            <LinearGradient
              {...gradients.accent}
              style={{
                width: `${readiness.percent}%`,
                height: "100%",
                borderRadius: 999,
              }}
            />
          </View>
        </View>

        <View style={{ gap: 7 }}>
          {readiness.sections.map((section) => {
            const Icon = section.complete ? CheckCircle2 : Circle;
            return (
              <View
                key={section.id}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Icon
                  size={15}
                  color={
                    section.complete
                      ? EMBER.colors.warm
                      : EMBER.colors.textLight
                  }
                  strokeWidth={1.8}
                />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12.5,
                    color: section.complete
                      ? EMBER.colors.text
                      : EMBER.colors.textLight,
                  }}
                >
                  {section.label}
                </Text>
              </View>
            );
          })}
        </View>

        {onOpenMedicalHistory ? (
          <CompactActionButton
            label={
              readiness.isReady
                ? "Review medical history"
                : "Continue medical history"
            }
            trailingIcon={ArrowRight}
            onPress={onOpenMedicalHistory}
            variant="secondary"
            minWidth={120}
          />
        ) : null}
      </CardSurface>
    );
  }

  return (
    <CardSurface style={{ marginTop: 16, padding: 20, gap: 10 }}>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 16,
            fontWeight: "600",
            color: EMBER.colors.text,
          }}
        >
          Estimated compensation
        </Text>
        <StatusBadge type="coming-soon" />
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          color: EMBER.colors.textLight,
          lineHeight: 20,
        }}
      >
        {ROLE_COPY[role]}
      </Text>
    </CardSurface>
  );
}
